import React, {useEffect, useState} from 'react';
import algoliasearch from 'algoliasearch/lite';
import {Configure, connectHits, InstantSearch} from 'react-instantsearch-dom';

import {
  ClearFilter,
  CurrentFilterDescriptor,
  Pagination,
  RangeSlider,
  RefinementList,
  RegionFilter,
  SearchBox,
  SortSelector,
} from '../algolia-widgets';
import {HighendCard} from './HighendCard';
import {QuickInfoModal} from './QuickInfoModal';
import {FilterBarLayout} from '../algolia-widgets/FilterBarLayout';
import {
  createURL,
  useSynchronizedQueryStringForSearchForHighends,
} from '../algolia-widgets/useSynchronizedQueryStringForSearchForHighends';
import {EmptyState} from "../EmptyState/EmptyState";

const searchClient = algoliasearch(
  process.env.MIX_ALGOLIA_APP_ID,
  process.env.MIX_ALGOLIA_PUBLIC_KEY
);

export function HighendsPage({ regions } = {}) {
  const [highendIdForModal, setHighendIdForModal] = useState(null);
  const { searchState, onSearchStateChange } =
    useSynchronizedQueryStringForSearchForHighends({
      refinementLists: ['skins', 'champions', 'rank.tier'],
      ranges: ['price', 'level'],
    });

  useEffect(() => {
    document.querySelector('#skeleton').style="display: none";
    document.querySelector('#sub-text').className="text-justify";
  }, []);

  return (
    <React.Fragment>
      {highendIdForModal && (
        <QuickInfoModal
          id={highendIdForModal}
          close={() => setHighendIdForModal(null)}
        />
      )}
      <InstantSearch
        indexName="accounts_by_id_asc"
        searchClient={searchClient}
        searchState={searchState}
        onSearchStateChange={onSearchStateChange}
        createURL={createURL}
      >
        <Configure hitsPerPage={32} />
        <FilterBarLayout
          clearFilter={ClearFilter}
          regionWidget={
            <RegionFilter attribute="region_name"
                          defaultRegions={['NA', 'EUW', 'EUNE', 'BR', 'OCE', 'LAN', 'JP', 'LAS', 'TR', 'RU']}/>
          }
          widgets={
            <React.Fragment>
              <SearchBox placeholder="Search our store" />
              <RangeSlider attribute="price" name="Price" unit="$" />
              <RefinementList
                attribute="skins"
                name="Skins"
                limit={100}
                operator="and"
                searchable
              />
              <RefinementList
                attribute="champions"
                name="Champions"
                limit={100}
                operator="and"
                searchable
              />
              <RangeSlider attribute="level" name="Level" side="left" />
              <RefinementList
                attribute="rank.tier"
                name="Rank"
                limit={100}
                side="left"
                operator="and"
                searchable
              />
            </React.Fragment>
          }
          bottomLeftWidgets={<CurrentFilterDescriptor />}
          bottomRightWidgets={
            <React.Fragment>
              <ClearFilter />
              <SortSelector
                defaultRefinement="accounts_by_relevance"
                items={[
                  { value: 'accounts_by_price_asc', label: 'Price|asc' },
                  { value: 'accounts_by_price_desc', label: 'Price|desc' },
                  { value: 'accounts_by_level_asc', label: 'Level|asc' },
                  { value: 'accounts_by_level_desc', label: 'Level|desc' },
                  {
                    value: 'accounts_by_skins_count_asc',
                    label: 'Number of Skins|asc',
                  },
                  {
                    value: 'accounts_by_skins_count_desc',
                    label: 'Number of Skins|desc',
                  },
                  {
                    value: 'accounts_by_champions_count_asc',
                    label: 'Number of Champions|asc',
                  },
                  {
                    value: 'accounts_by_champions_count_desc',
                    label: 'Number of Champions|desc',
                  },
                ]}
              />
            </React.Fragment>
          }
        />

        <HighendsList setHighendIdForModal={setHighendIdForModal} />

        <Pagination />
      </InstantSearch>
    </React.Fragment>
  );
}

const HighendsList = connectHits(({ hits, setHighendIdForModal }) => {
  if (hits.length === 0) {
    return (
      <div className="valorant-category-page__search-container">
        <EmptyState />
      </div>
    )}

  const products = hits.map(({__position, url, title, image_url, blue_essence, champions_count}) => ({
    "@type": "ListItem",
    "position": __position,
    "url": url,
    "name": title,
    "description": `Take a look at this LoL account that includes ${blue_essence} blue essence and ${champions_count} champions including ${title}`,
    "image": image_url
  }))

  document.querySelector('#schema-itemlist').innerHTML = JSON.stringify(
  {
    "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": products
  }, null, 3)


  return (
    <div className="flex flex-wrap mt-8 -ml-4 content-start">
      {hits.map((highend, index) => (
        <HighendCard
          key={highend.id}
          highend={highend}
          openQuickInfoModal={() => {
            setHighendIdForModal(highend.id);
          }}
          index={index}
        />
      ))}
    </div>
  );
});
